/**
 * Recharts theming module — Institutional Console redesign (issue #234).
 *
 * Centralizes the Recharts visual language specified in
 * `redesign/DESIGN-SPEC.md` §4.5 so every chart (Dashboard performance line,
 * Analytics donut, Cash flow bars, sparklines, etc.) draws from one source of
 * truth instead of per-screen drift. Per DESIGN-SPEC §8 ("Charts: keep
 * Recharts; apply §4.5"), this module does NOT replace Recharts — it exports
 * plain values/components that screens pass into existing `<Area>`, `<Line>`,
 * `<Pie>`, `<CartesianGrid>`, `<XAxis>`, `<YAxis>`, and `<Tooltip>` props.
 *
 * Spec §4.5 requirements encoded here:
 * - Portfolio/primary line: `--accent` 2px stroke with a 20%→0 vertical
 *   gradient fill (`chartGradientStops`, `CHART_LINE.strokeWidth`).
 * - Benchmark/secondary lines: 1.5px dashed `5 4`, colors drawn ONLY from
 *   chart-neutral-1/2/gold — NEVER semantic green/red (`BENCHMARK_COLORS`,
 *   `CHART_BENCHMARK_LINE`).
 * - Grid: horizontal-only hairline (`chartGridProps`).
 * - Axis ticks: JetBrains Mono 10px `--text-faint` (`chartAxisTickStyle`).
 * - No dots on lines (`CHART_LINE.dot = false`).
 * - Tooltip: surface bg + border + mono values, 80ms fade
 *   (`ChartTooltipContent`, `chartTooltipAnimationDuration`).
 * - Donut: 24px ring width, 2° gaps between slices, slices drawn from
 *   accent/positive/chart-neutral-1/chart-gold (`DONUT_SLICE_COLORS`,
 *   `donutRingProps`).
 * - Sparklines: 1.5px stroke, no axes, no fill (`SPARKLINE_LINE`).
 * - Motion: §6 — chart draw-in is disabled (render final state), tooltip
 *   fade is the only chart transition, and it must respect
 *   `prefers-reduced-motion` (`getChartAnimationProps`).
 *
 * These are CSS custom-property references (`var(--accent)`, etc.) so charts
 * automatically re-theme across `console-dark`/`console-light`/legacy themes
 * without this module needing per-theme branches — the same pattern used by
 * `tailwind.config.js`'s `chart-neutral-1/2`/`chart-gold` keys (CC3).
 */

/** Portfolio/primary series color (spec §4.5: "Portfolio line: --accent"). */
export const CHART_ACCENT_COLOR = "var(--accent, var(--color-primary-500))";

/**
 * Benchmark/neutral series palette. Spec §4.5 + §7: "Benchmarks: ... never
 * semantic green/red" — this palette is the ONLY set of colors permitted for
 * benchmark/comparison series. Consumers must not substitute --positive/
 * --negative here even for a "beating benchmark" visual cue.
 */
export const BENCHMARK_COLORS = [
  "var(--chart-neutral-1, var(--color-text-tertiary))",
  "var(--chart-neutral-2, var(--color-text-secondary))",
  "var(--chart-gold, var(--color-warning-500))",
];

/** Donut/pie slice palette, in spec order (§4.5: "accent/positive/neutral-1/gold"). */
export const DONUT_SLICE_COLORS = [
  "var(--accent, var(--color-primary-500))",
  "var(--positive, var(--color-success-500))",
  "var(--chart-neutral-1, var(--color-text-tertiary))",
  "var(--chart-gold, var(--color-warning-500))",
];

/** Gradient stop config for the primary line's area fill (20% → 0 opacity, vertical). */
export const chartGradientStops = [
  { offset: "0%", stopColor: CHART_ACCENT_COLOR, stopOpacity: 0.2 },
  { offset: "100%", stopColor: CHART_ACCENT_COLOR, stopOpacity: 0 },
];

/** Props for the primary/portfolio `<Line>` or `<Area>` stroke — no dots, 2px, solid. */
export const CHART_LINE = {
  stroke: CHART_ACCENT_COLOR,
  strokeWidth: 2,
  dot: false,
  activeDot: { r: 3, strokeWidth: 0 },
};

/** Props for benchmark `<Line>` series — 1.5px dashed `5 4`, no dots. */
export const CHART_BENCHMARK_LINE = {
  strokeWidth: 1.5,
  strokeDasharray: "5 4",
  dot: false,
  activeDot: false,
};

/** Props for sparkline `<Line>` — 1.5px, no dots, no axes, no fill (rendered separately). */
export const SPARKLINE_LINE = {
  strokeWidth: 1.5,
  dot: false,
  isAnimationActive: false,
};

/**
 * `<CartesianGrid>` props — horizontal-only hairline grid (spec §4.5).
 * `vertical={false}` removes vertical rules entirely rather than dimming them.
 */
export const chartGridProps = {
  horizontal: true,
  vertical: false,
  stroke: "var(--hairline, var(--color-border-subtle))",
};

/**
 * Shared axis tick style — JetBrains Mono 10px `--text-faint` (spec §4.5/§2.3).
 * Pass as the `tick` prop's nested `style`, e.g.
 * `<XAxis tick={{ style: chartAxisTickStyle }} />` or spread into a custom
 * tick renderer's `<text style={chartAxisTickStyle}>`.
 */
export const chartAxisTickStyle = {
  fontFamily:
    "'JetBrains Mono', var(--font-mono), monospace",
  fontSize: 10,
  fill: "var(--text-faint, var(--color-text-disabled))",
};

/** Shared `<XAxis>`/`<YAxis>` line-suppression props (ticks only, no axis line). */
export const chartAxisLineProps = {
  tickLine: false,
  axisLine: false,
};

/**
 * Donut ring geometry — 24px ring width, 2° gaps between slices (spec §4.5).
 * `outerRadius` defaults to 62, matching `redesign/Analytics.dc.html`'s
 * `donutArc(70, 70, 62, 38, ...)` call (`cx=cy=70` is the 140×140 viewBox
 * center; 62 is the actual outer ring radius) — pass an explicit
 * `outerRadius` for any container size that differs from that mockup.
 */
export function donutRingProps({ outerRadius = 62 } = {}) {
  const RING_WIDTH = 24;
  return {
    outerRadius,
    innerRadius: Math.max(outerRadius - RING_WIDTH, 0),
    paddingAngle: 2,
    startAngle: 90,
    endAngle: -270,
  };
}

/** Tooltip fade duration in ms (spec §4.5/§6: "tooltip fade 80ms"). */
export const CHART_TOOLTIP_FADE_MS = 80;

/**
 * Returns Recharts animation props honoring `prefers-reduced-motion` (spec
 * §6: "Chart draw-in: none (render final state)"). Draw-in animation is
 * disabled unconditionally per spec — this helper exists so a future
 * `animate` opt-in (if design ever wants micro-interactions back) has a
 * single, reduced-motion-safe seam. Currently always returns
 * `isAnimationActive: false` regardless of the flag, matching the current
 * spec's "no draw-in" rule; `prefersReducedMotion` is accepted for API
 * symmetry with `getChartAnimationProps` call sites and to make the
 * reduced-motion contract explicit at call sites/tests.
 *
 * @param {boolean} [prefersReducedMotion] - value from usePrefersReducedMotion()
 */
export function getChartAnimationProps(prefersReducedMotion) {
  void prefersReducedMotion; // always false per spec §6; kept for signature clarity
  return { isAnimationActive: false };
}

/**
 * Shared tooltip content styling — surface bg, border, mono values (spec
 * §4.5: "tooltip on hover (surface bg, border, mono values)"). This is a
 * plain style-object export (not a component) so screens with bespoke
 * tooltip content (e.g. multi-series benchmark tooltips) can still share the
 * chrome. See `ChartTooltipShell` below for a ready-made wrapper.
 */
export const chartTooltipContainerStyle = {
  backgroundColor: "var(--surface, var(--color-surface))",
  border: "1px solid var(--border, var(--color-border))",
  borderRadius: "6px",
  padding: "8px 10px",
  fontSize: "11px",
  boxShadow: "var(--shadow-dropdown, var(--shadow-lg))",
};

export const chartTooltipLabelStyle = {
  color: "var(--text-faint, var(--color-text-disabled))",
  fontFamily: "'JetBrains Mono', var(--font-mono), monospace",
  fontSize: "10px",
  marginBottom: "4px",
};

export const chartTooltipValueStyle = {
  fontFamily: "'JetBrains Mono', var(--font-mono), monospace",
  color: "var(--text, var(--color-text-primary))",
};
